import api from './api';

export interface Registro {
  id: number;
  /** Rastreio do match automático — presente só em origem MONITORAMENTO. */
  gravacao_id?: number;
  audio_id?: number;
  offset_segundos?: number;
  datahora?: string;
  dataabsoluta?: string;
  numero?: number;
  duracao?: number;
  tipo?: string;
  break1?: number;
  posicao?: number;
  custo?: number;
  titulo?: string;
  qualidade?: string;
  genero?: string;
  mercado?: string;
  emissora_id?: number;
  marca_id?: number;
  programa_id?: number;
  anunciante_id?: number;
  agencia_id?: number;
  setor_id?: number;
  categoria_id?: number;
  item_id?: number;
  patrocinador_id?: number;
  precisao?: number;
  sincronia?: number;
  dispersao?: number;
  consideracoes?: string;
  origem?: string;
  pi_number?: string;
  // Campos relacionados que podem vir nas consultas
  emissora?: { id: number, nome: string };
  marca?: { id: number, nome: string };
  programa?: { id: number, nome: string };
  anunciante?: { id: number, nome: string };
}

export interface RegistroCreate {
  datahora?: string;
  dataabsoluta?: string;
  numero?: number;
  duracao?: number;
  tipo?: string;
  break1?: number;
  posicao?: number;
  custo?: number;
  titulo?: string;
  qualidade?: string;
  genero?: string;
  mercado?: string;
  emissora_id?: number;
  marca_id?: number;
  programa_id?: number;
  anunciante_id?: number;
  agencia_id?: number;
  setor_id?: number;
  categoria_id?: number;
  item_id?: number;
  patrocinador_id?: number;
  precisao?: number;
  sincronia?: number;
  dispersao?: number;
  consideracoes?: string;
  origem?: string;
  pi_number?: string;
}

export interface RegistroUpdate {
  datahora?: string;
  dataabsoluta?: string;
  numero?: number;
  duracao?: number;
  tipo?: string;
  break1?: number;
  posicao?: number;
  custo?: number;
  titulo?: string;
  qualidade?: string;
  genero?: string;
  mercado?: string;
  emissora_id?: number;
  marca_id?: number;
  programa_id?: number;
  anunciante_id?: number;
  agencia_id?: number;
  setor_id?: number;
  categoria_id?: number;
  item_id?: number;
  patrocinador_id?: number;
  precisao?: number;
  sincronia?: number;
  dispersao?: number;
  consideracoes?: string;
  origem?: string;
  pi_number?: string;
}

// Link do áudio da rádio de um registro (bloco de ~15 min da gravação)
export interface AudioLink {
  disponivel: boolean;
  motivo?: string;
  token?: string;
  caminho?: string;               // relativo: /api/v1/registros/audio/<token>
  offset_segundos?: number;       // onde a inserção começa dentro do bloco
  gravacao_id?: number;
  inicio_bloco?: string;
  duracao_bloco_segundos?: number;
}

export interface RegistroFilter {
  titulo?: string;
  tipo?: string;
  pi_number?: string;
  duracao?: number;
  duracao_min?: number;
  duracao_max?: number;
  datahora_inicio?: string;
  datahora_fim?: string;
  mercado?: string;
  qualidade?: string;
  genero?: string;
  emissora_id?: number;
  emissora_ids?: number[];  // Added array of emissora IDs
  marca_id?: number;
  programa_id?: number;
  anunciante_id?: number;
  item_id?: number;
  setor_id?: number;
  agencia_id?: number;
  categoria_id?: number;
}

// Query params do filtro para a API. O paramsSerializer de api.ts usa
// URLSearchParams, que transforma undefined em "undefined" (a API responde 422
// e o relatório vem vazio) e array em "1,2,3" — então descartamos os vazios e
// mandamos listas já no formato "1,2,3", que é o que o backend aceita.
const montarParamsFiltro = (filter: RegistroFilter): Record<string, string | number> => {
  const params: Record<string, string | number> = {};
  Object.entries(filter).forEach(([chave, valor]) => {
    if (valor === undefined || valor === null || valor === '') {
      return;
    }
    if (Array.isArray(valor)) {
      if (valor.length > 0) {
        params[chave] = valor.join(',');
      }
      return;
    }
    params[chave] = valor;
  });
  // Seleção múltipla de emissoras prevalece sobre a emissora única
  if (params.emissora_ids !== undefined) {
    delete params.emissora_id;
  }
  return params;
};

export const RegistrosService = {
  getRegistros: async (filter: RegistroFilter = {}, skip = 0, limit = 100) => {
    try {
      console.log('[RegistrosService] Attempting to getRegistros.');
      
      // Create the request parameters
      const params = { skip, limit, ...montarParamsFiltro(filter) };
      console.log('[RegistrosService] Request params after processing:', params);
      
      // CRITICAL FIX: Use a relative path that inherits the page's protocol
      // This is the key to avoiding mixed content errors
      const endpoint = '/registros';
      
      // Log before making the request
      console.log(`[RegistrosService] Making request to relative endpoint: ${endpoint}`);
      console.log(`[RegistrosService] Current page protocol: ${window.location.protocol}`);
      
      // Make the request using the relative path
      const response = await api.get(endpoint, { 
        params: params,
        baseURL: '/api/v1' // Force using a relative baseURL
      });
      
      // For debugging - log the actual request details
      console.log('[RegistrosService] Response received!');
      
      // Check if we got a successful response with data
      if (response.data) {
        console.log('[RegistrosService] Registros received successfully:', 
          Array.isArray(response.data) ? `${response.data.length} items` : 'data object');
        return response.data;
      } else {
        console.warn('[RegistrosService] Received empty response data');
        return [];
      }
    } catch (error: any) {
      console.error('[RegistrosService] Error fetching registros:', error);
      
      // Enhanced error logging for debugging mixed content issues
      if (error.response) {
        console.error('[RegistrosService] Error Response:', error.response.data);
        console.error('[RegistrosService] Error Status:', error.response.status);
      } else if (error.request) {
        // Error with request - likely mixed content or connection issue
        console.error('[RegistrosService] Network Error Details:');
        console.error(`- Request URL: ${error.config?.url || 'unknown'}`);
        console.error(`- Base URL: ${error.config?.baseURL || 'unknown'}`);
        console.error(`- Full URL: ${error.config?.baseURL}${error.config?.url}`);
        console.error(`- Method: ${error.config?.method || 'unknown'}`);
        
        // Check for mixed content issues
        const fullUrl = `${error.config?.baseURL || ''}${error.config?.url || ''}`;
        if (fullUrl.startsWith('http:') && window.location.protocol === 'https:') {
          console.error('⚠️ MIXED CONTENT ISSUE DETECTED: Trying to make HTTP request from HTTPS page');
          console.error('Solution: Ensure all URLs are either relative or use HTTPS protocol');
        }
      }
      
      // Return empty array instead of throwing to prevent UI from breaking completely
      console.error('[RegistrosService] Returning empty array due to error');
      return [];
    }
  },

  // Relatórios: busca em blocos (skip/limit) até `limite`. Diferente de
  // getRegistros, propaga o erro — um bloco que falha no meio não pode virar
  // um relatório incompleto sem aviso.
  getRegistrosEmBlocos: async (filter: RegistroFilter = {}, limite = 50000, tamanhoBloco = 10000): Promise<Registro[]> => {
    const params = montarParamsFiltro(filter);
    let registros: Registro[] = [];
    while (registros.length < limite) {
      const limit = Math.min(tamanhoBloco, limite - registros.length);
      const response = await api.get('/registros', {
        params: { skip: registros.length, limit, ...params },
        baseURL: '/api/v1',
      });
      const bloco: Registro[] = Array.isArray(response.data) ? response.data : [];
      registros = registros.concat(bloco);
      if (bloco.length < limit) {
        break;
      }
    }
    return registros;
  },

  getRegistroCount: async (filter: RegistroFilter = {}) => {
    try {
      console.log('[RegistrosService] Attempting to getRegistroCount.');
      
      // Create the request parameters
      const params = montarParamsFiltro(filter);
      console.log('[RegistrosService] Request params for count after processing:', params);
      
      // CRITICAL FIX: Use a relative path that inherits the page's protocol
      // This is the key to avoiding mixed content errors
      const endpoint = '/registros/count';
      
      // Log before making the request
      console.log(`[RegistrosService] Making count request to relative endpoint: ${endpoint}`);
      console.log(`[RegistrosService] Current page protocol: ${window.location.protocol}`);
      
      // Make the request using the relative path
      const response = await api.get(endpoint, { 
        params: params,
        baseURL: '/api/v1' // Force using a relative baseURL
      });
      
      // For debugging - log the actual request details
      console.log('[RegistrosService] Count response received!');
      
      // Return the count value
      console.log('[RegistrosService] Count received successfully:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[RegistrosService] Error fetching registro count:', error);
      if (error.response) {
        console.error('[RegistrosService] Error Response (count):', error.response.data);
      } else if (error.request) {
        console.error('[RegistrosService] Error Request Config (count):', error.config);
      } else {
        console.error('[RegistrosService] Error Message (count):', error.message);
      }
      console.error('[RegistrosService] Full Error Config (count):', error.config);
      throw error;
    }
  },

  getRegistro: async (id: number) => {
    const response = await api.get(`/registros/${id}`);
    return response.data;
  },

  /** Áudio da gravação em que a veiculação foi detectada, com o segundo exato.
   *  Existe só em registro criado pelo audio-matcher; nos demais devolve 404. */
  getAudioVeiculacao: async (id: number) => {
    const response = await api.get(`/registros/${id}/audio`);
    return response.data;
  },

  createRegistro: async (registro: RegistroCreate) => {
    console.log('=== RegistrosService.createRegistro ===');
    console.log('Dados recebidos pelo service:', JSON.stringify(registro, null, 2));
    console.log('pi_number no service:', registro.pi_number);
    const response = await api.post('/registros', registro);
    console.log('Resposta do servidor:', JSON.stringify(response.data, null, 2));
    return response.data;
  },

  updateRegistro: async (id: number, registro: RegistroUpdate) => {
    const response = await api.put(`/registros/${id}`, registro);
    return response.data;
  },

  deleteRegistro: async (id: number) => {
    const response = await api.delete(`/registros/${id}`);
    return response.data;
  },

  getTitulos: async (q = '', limit = 20): Promise<string[]> => {
    try {
      const response = await api.get('/registros/titulos', {
        params: { q, limit },
        baseURL: '/api/v1',
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('[RegistrosService] Erro ao buscar títulos:', error);
      return [];
    }
  },

  getSugestaoMarca: async (marcaId: number): Promise<Partial<RegistroCreate>> => {
    try {
      const response = await api.get(`/registros/sugestao-marca/${marcaId}`, {
        baseURL: '/api/v1',
      });
      return response.data || {};
    } catch (error) {
      console.error('[RegistrosService] Erro ao buscar sugestão por marca:', error);
      return {};
    }
  },

  getItensPorMarca: async (marcaId: number): Promise<Array<{ id: number; nome: string }>> => {
    try {
      const response = await api.get(`/registros/itens-marca/${marcaId}`, {
        baseURL: '/api/v1',
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('[RegistrosService] Erro ao buscar itens da marca:', error);
      return [];
    }
  },

  getTipos: async () => {
    const response = await api.get('/registros/tipos');
    return response.data;
  },

  getQualidades: async () => {
    const response = await api.get('/registros/qualidades');
    return response.data;
  },

  getGeneros: async () => {
    const response = await api.get('/registros/generos');
    return response.data;
  },

  getMercados: async () => {
    const response = await api.get('/registros/mercados');
    return response.data;
  },

  filterRegistros: async (filter: RegistroFilter = {}, skip = 0, limit = 100) => {
    const response = await api.post('/registros/filter', filter, {
      params: { skip, limit }
    });
    return response.data;
  },

  // Áudio da rádio: para cada registro diz se existe gravação e devolve o link.
  // Uma chamada só para o relatório inteiro — a URL assinada da Eveo é gerada
  // no backend a cada acesso ao link, então ele não expira junto com o PDF.
  getAudioLinks: async (ids: number[], validadeDias?: number): Promise<Record<string, AudioLink>> => {
    if (!ids.length) return {};
    // O backend aceita no máximo 2000 registros por chamada. Antes ia a lista
    // inteira: acima disso vinha 400 e a coluna de áudio sumia sem aviso.
    const TAMANHO_LOTE = 2000;
    const links: Record<string, AudioLink> = {};
    for (let i = 0; i < ids.length; i += TAMANHO_LOTE) {
      const response = await api.post('/registros/audio/links', {
        registro_ids: ids.slice(i, i + TAMANHO_LOTE),
        validade_dias: validadeDias,
      });
      Object.assign(links, response.data);
    }
    return links;
  },

  // Transforma o caminho relativo devolvido pela API em URL absoluta — o link
  // precisa disso para funcionar quando o PDF é aberto fora do navegador.
  urlAbsolutaAudio: (link?: AudioLink): string | undefined =>
    link?.disponivel && link.caminho ? `${window.location.origin}${link.caminho}` : undefined,

  getRegistroById: async (id: number): Promise<Registro> => {
    try {
      console.log(`[RegistrosService] Attempting to getRegistroById: ${id}.`);
      console.log('[RegistrosService] Imported api instance baseURL:', api.defaults.baseURL);
      const response = await api.get(`/registros/${id}`);
      console.log(`[RegistrosService] Registro ${id} received successfully:`, response.data);
      console.log('[RegistrosService] Axios effective URL:', response.config.url);
      console.log('[RegistrosService] Axios effective baseURL:', response.config.baseURL);
      return response.data;
    } catch (error: any) {
      console.error(`[RegistrosService] Error fetching registro ${id}:`, error);
      if (error.response) {
        console.error('[RegistrosService] Error Response:', error.response.data);
      } else if (error.request) {
        console.error('[RegistrosService] Error Request Config:', error.config);
      } else {
        console.error('[RegistrosService] Error Message:', error.message);
      }
      console.error('[RegistrosService] Full Error Config:', error.config);
      throw error;
    }
  },
};

export default RegistrosService; 